import { ConditionOperatorEnum, type Condition } from '@repo/api-gw-sdk';

import {
  createCombineCondition,
  isCombineCondition,
  isValueCondition,
} from '@/types/advanceFilter';

/**
 * Which status property a helper works on: `backupStatus`, or `configurationStatus` once
 * `InventoryStatusColumns` moves the scope there. `status` is that property's own value.
 */
interface StatusProperty {
  property: string;
  status: string;
}

/**
 * Backup status conditions sit at the query's root, the same level the filter bar
 * writes them to, so these helpers stay there too.
 */
const rootOf = (query: Condition | undefined) =>
  isCombineCondition(query) ? query! : createCombineCondition();

const isStatusCondition = (condition: Condition, property: string) =>
  isValueCondition(condition) && condition.property === property;

/** Whether any backup status condition in the query mentions `status`. */
export const hasBackupStatus = (
  query: Condition | undefined,
  { property, status }: StatusProperty
) =>
  (rootOf(query).conditions ?? []).some(
    (condition) =>
      isStatusCondition(condition, property) &&
      condition.value?.includes(status)
  );

/**
 * Puts `status` out of the query's reach, leaving every other filter alone.
 *
 * The request keeps one rule per property — `convertQuery` assigns rather than
 * merges — so a second backup status condition alongside the user's would decide
 * the request on its own and throw their filter away. The exclusion therefore goes
 * *into* the conditions already there: added to what a `not in` excludes, taken
 * out of what anything else selects. Every backup status condition is rewritten,
 * so whichever one the request ends up keeping still leaves `status` out.
 */
export const withBackupStatusExcluded = (
  query: Condition | undefined,
  { property, status }: StatusProperty
): Condition => {
  const root = rootOf(query);

  const conditions = (root.conditions ?? []).flatMap((condition) => {
    if (!isStatusCondition(condition, property)) {
      return [condition];
    }

    if (condition.operator === ConditionOperatorEnum.NotIn) {
      return condition.value?.includes(status)
        ? [condition]
        : [{ ...condition, value: [...(condition.value ?? []), status] }];
    }

    // A selection of other statuses leaves this one out by itself. Emptied by the
    // removal it selects nothing at all, so it goes and the exclusion below stands
    // in for it.
    const value = (condition.value ?? []).filter((x) => x !== status);
    return value.length ? [{ ...condition, value }] : [];
  });

  return {
    ...root,
    conditions: conditions.some((condition) =>
      isStatusCondition(condition, property)
    )
      ? conditions
      : [
          ...conditions,
          { property, operator: ConditionOperatorEnum.NotIn, value: [status] },
        ],
  };
};

/**
 * Drops `status` from the query's backup status conditions — both the excluded
 * and the selected ones — and with it any condition left without a value.
 */
export const withoutBackupStatus = (
  query: Condition | undefined,
  { property, status }: StatusProperty
): Condition => {
  const root = rootOf(query);

  return {
    ...root,
    conditions: (root.conditions ?? []).flatMap((condition) => {
      if (
        !isStatusCondition(condition, property) ||
        !condition.value?.includes(status)
      ) {
        return [condition];
      }

      const value = condition.value.filter((x) => x !== status);
      return value.length ? [{ ...condition, value }] : [];
    }),
  };
};
